/**
 * Shareable-link + file persistence (spec §20).
 *
 * Link: Plan -> JSON -> gzip (native CompressionStream) -> base64url -> URL
 * FRAGMENT (after '#'). Fragments are never transmitted to servers and do not
 * appear in server/CDN logs or referrer headers — this is what makes link
 * sharing consistent with the no-data-leaves-the-browser constraint.
 *
 * Both mechanisms are user-initiated. Nothing runs automatically.
 */
import type { Plan } from '../model/types';
import { migratePlan } from '../model/migrations';
import { isPlanShape } from '../model/validation';
import { newScenarioId, type Scenario } from '../scenarios/scenario';

/** A decoded payload: the plan plus any scenarios that traveled with it. */
export interface PlanBundle {
  plan: Plan;
  scenarios: Scenario[];
}

// ── base64url ───────────────────────────────────────────────────────────────

function bytesToBase64url(bytes: Uint8Array): string {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// ── gzip via native streams ─────────────────────────────────────────────────

async function gzip(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function gunzip(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// ── public API ──────────────────────────────────────────────────────────────

/** Serialize a plan (+ scenarios, spec §15) to a fragment payload (no '#'). */
export async function encodePlanToFragment(plan: Plan, scenarios: Scenario[] = []): Promise<string> {
  const payload =
    scenarios.length > 0 ? { _bundle: 1, plan, scenarios } : plan;
  const json = JSON.stringify(payload);
  const compressed = await gzip(new TextEncoder().encode(json));
  return bytesToBase64url(compressed);
}

/**
 * Decode a fragment payload back into a plan + scenarios, running the
 * migration chain on each plan. Accepts both the bundle format and older
 * bare-plan payloads (someone will open a six-month-old link — it must work).
 * Throws with a human-readable message on any failure.
 */
export async function decodePlanFromFragment(fragment: string): Promise<PlanBundle> {
  const cleaned = fragment.replace(/^#/, '').trim();
  if (!cleaned) throw new Error('The link contains no plan data.');

  let raw: unknown;
  try {
    const bytes = base64urlToBytes(cleaned);
    const json = new TextDecoder().decode(await gunzip(bytes));
    raw = JSON.parse(json);
  } catch {
    throw new Error(
      'This link could not be read. It may be incomplete — check that the whole link was copied.',
    );
  }

  return hydrateBundle(raw);
}

/** Build the JSON file payload (schema version + export timestamp included). */
export function planToFileJson(plan: Plan, constantsAsOf: string, scenarios: Scenario[] = []): string {
  return JSON.stringify(
    {
      _format: 'retirement-planner-plan',
      exportedAt: new Date().toISOString(),
      taxConstantsAsOf: constantsAsOf,
      plan,
      ...(scenarios.length > 0 ? { scenarios } : {}),
    },
    null,
    2,
  );
}

/** Parse an uploaded JSON file. Fails loudly with a specific message. */
export function planFromFileJson(text: string): PlanBundle {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('That file is not valid JSON.');
  }
  const obj = parsed as Record<string, unknown>;
  if (obj?._format === 'retirement-planner-plan') {
    return {
      plan: hydratePlan(obj.plan),
      scenarios: hydrateScenarios(obj.scenarios),
    };
  }
  return hydrateBundle(parsed);
}

function hydrateBundle(raw: unknown): PlanBundle {
  const obj = raw as Record<string, unknown>;
  if (obj && typeof obj === 'object' && obj._bundle === 1) {
    return { plan: hydratePlan(obj.plan), scenarios: hydrateScenarios(obj.scenarios) };
  }
  return { plan: hydratePlan(raw), scenarios: [] };
}

function hydrateScenarios(raw: unknown): Scenario[] {
  if (!Array.isArray(raw)) return [];
  const out: Scenario[] = [];
  // Ids must be unique or the UI edits two scenarios at once. Files written by
  // older versions can contain repeats, so re-mint rather than trust.
  const seen = new Set<string>();
  for (const item of raw) {
    const s = item as Record<string, unknown>;
    if (typeof s?.name !== 'string' || typeof s?.plan !== 'object') continue;
    try {
      const plan = hydratePlan(s.plan);
      const claimed = typeof s.id === 'string' && s.id.length > 0 ? s.id : null;
      const id = claimed && !seen.has(claimed) ? claimed : newScenarioId();
      seen.add(id);
      out.push({ id, name: s.name, plan });
    } catch {
      // A single corrupt scenario shouldn't sink the whole import.
    }
  }
  return out;
}

function hydratePlan(raw: unknown): Plan {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('The plan data is not in a recognized format.');
  }
  const { plan } = migratePlan(raw as Record<string, unknown>);
  if (!isPlanShape(plan)) {
    throw new Error(
      'The plan is missing required sections (household, accounts, budget). It may be from an incompatible version.',
    );
  }
  return plan;
}
